import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

interface HeaderColumnConfig {
  key: string;
  label?: string;
  type?: 'text' | 'dropdown' | 'input';
  isDropdown?: boolean;
  options?: string[];
  optionsField?: string;
  isMultiSelect?: boolean;
  placeholder?: string;
  inputType?: string;
}

interface LegacyDiscountMappingConfig {
  discountType: string;
  quoteLevelDiscount: string;
  reasons: string[];
  defaultSelectedReason?: string;
  defaultSelectedReasons?: string[];
}

interface SelectionMappingConfig {
  value?: string;
  triggerValue?: string;
  selectionValue?: string;
  row?: RowData;
  rows?: RowData[];
}

type RowData = Record<string, any>;

@Component({
  selector: 'app-table-w-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatFormFieldModule, MatSelectModule, MatInputModule],
  templateUrl: './table-w-dropdown.component.html',
  styleUrl: './table-w-dropdown.component.scss'
})
export class TableWDropdownComponent implements OnDestroy {
  private _data: any;
  private filterSubscription?: Subscription;
  private widgetDataSubscription?: Subscription;
  private resetStateSubscription?: Subscription;
  private baseDataSource: RowData[] = [];
  private selectedRowsByValue: Map<string, RowData[]> = new Map();
  private mappingRowsByValue: Record<string, RowData[]> = {};
  private dynamicFilterKey = '';
  private selectionMode: 'replace' | 'append' = 'replace';

  @Input() set data(value: any) {
    this._data = value;
    this.initializeTable();
  }
  get data(): any {
    return this._data;
  }

  displayedColumns: string[] = [];
  columnLabels: Record<string, string> = {};
  dataSource: RowData[] = [];
  dropdownColumnConfig: Record<string, HeaderColumnConfig> = {};
  columnConfigMap: Record<string, HeaderColumnConfig> = {};
  private readonly defaultFooterLineText = 'Approval triggers if cumulative discounts greater than 20%';
  private readonly compactColumnKeys = new Set<string>([
    'line_discount',
    'quote_level_discount'
  ]);
  private readonly wideColumnKeys = new Set<string>([
    'condition',
    'description'
  ]);
  private readonly numericColumnHints = [
    'line_discount',
    'quote_level_discount',
    'discounted_price',
    'unit_price',
    'price',
    'amount',
    'sales',
    'impact',
    'total',
    'qty',
    'quantity',
    'rate',
    'percent',
    'percentage',
    'usd'
  ];
  private readonly nonNumericColumnHints = [
    'discount_type',
    'type',
    'reason',
    'description',
    'name',
    'clause',
    'condition',
    'bundle'
  ];

  constructor(private globalFilterService: GlobalFilterService) { }

  get shouldShowFooterLine(): boolean {
    const rawValue = this.data?.add_line ?? this.data?.addLine;

    if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    if (typeof rawValue === 'number') {
      return rawValue === 1;
    }

    return rawValue === true;
  }

  get footerLineText(): string {
    const configuredText = String(this.data?.add_line_text ?? this.data?.addLineText ?? '').trim();
    return configuredText || this.defaultFooterLineText;
  }

  isDropdownColumn(columnKey: string): boolean {
    return !!this.dropdownColumnConfig[columnKey];
  }

  isInputColumn(columnKey: string): boolean {
    return this.columnConfigMap[columnKey]?.type === 'input';
  }

  isMultiSelect(columnKey: string): boolean {
    return !!this.dropdownColumnConfig[columnKey]?.isMultiSelect;
  }

  getInputType(columnKey: string, row?: RowData): string {
    const configured = String(this.columnConfigMap[columnKey]?.inputType || '').trim();
    if (this.isNumericColumn(columnKey, row)) {
      // Keep numeric fields as text inputs so comma-separated values can be shown/edited.
      return 'text';
    }

    if (configured) {
      return configured;
    }

    return 'text';
  }

  getInputPlaceholder(columnKey: string): string {
    const configured = String(this.columnConfigMap[columnKey]?.placeholder || '').trim();
    if (configured) {
      return configured;
    }

    const label = String(this.columnLabels?.[columnKey] || columnKey || '').trim();
    return label ? `Enter ${label}` : 'Enter value';
  }

  public isCompactColumn(columnKey: string): boolean {
    return this.compactColumnKeys.has(String(columnKey || '').trim().toLowerCase());
  }

  isWideColumn(columnKey: string): boolean {
    return this.wideColumnKeys.has(String(columnKey || '').trim().toLowerCase());
  }

  public isNumericColumn(columnKey: string, row?: RowData): boolean {
    if (!columnKey) {
      return false;
    }

    if (this.isLikelyNumericColumn(columnKey)) {
      return true;
    }

    return this.isNumericValue(row?.[columnKey]);
  }

  public getHeaderCellClasses(columnKey: string): Record<string, boolean> {
    return {
      'compact-column': this.isCompactColumn(columnKey),
      'wide-column': this.isWideColumn(columnKey),
      'numeric-column': this.isNumericColumn(columnKey)
    };
  }

  public getBodyCellClasses(columnKey: string, row: RowData): Record<string, boolean> {
    return {
      'compact-column': this.isCompactColumn(columnKey),
      'wide-column': this.isWideColumn(columnKey),
      'numeric-column': this.isNumericColumn(columnKey, row),
      'dropdown-column': this.isDropdownColumn(columnKey)
    };
  }

  public formatHeaderLabel(value: any): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }

    const normalized = raw
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized
      .split(' ')
      .map((word) => {
        if (!word) {
          return word;
        }

        const leading = (word.match(/^[^A-Za-z0-9]*/) || [''])[0];
        const trailing = (word.match(/[^A-Za-z0-9]*$/) || [''])[0];
        const core = word.slice(leading.length, word.length - trailing.length);
        if (!core) {
          return word;
        }

        if (/^[A-Z0-9]{1,3}$/.test(core)) {
          return `${leading}${core}${trailing}`;
        }

        const lowerCore = core.toLowerCase();
        const titleCore = lowerCore.charAt(0).toUpperCase() + lowerCore.slice(1);
        return `${leading}${titleCore}${trailing}`;
      })
      .join(' ');
  }

  getDropdownOptions(row: RowData, columnKey: string): string[] {
    const columnConfig = this.dropdownColumnConfig[columnKey];
    if (!columnConfig) {
      return [];
    }

    const rowOptionsField = columnConfig.optionsField || `${columnKey}Options`;
    const rowOptions = rowOptionsField && Array.isArray(row?.[rowOptionsField]) ? row[rowOptionsField] : [];
    const columnOptions = Array.isArray(columnConfig.options) ? columnConfig.options : [];

    const mergedOptions = [...rowOptions, ...columnOptions]
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);

    return Array.from(new Set(mergedOptions));
  }

  onDropdownChange(row: RowData, columnKey: string, value: any): void {
    row[columnKey] = this.normalizeValueForColumn(columnKey, value);
    this.syncWorkflowCtxForColumn(columnKey, row[columnKey]);
    this.publishWidgetDataIfEnabled();
  }

  onTextInputChange(row: RowData, columnKey: string, value: any): void {
    if (this.isNumericColumn(columnKey, row)) {
      const normalized = this.normalizeNumericForStorage(value);
      row[columnKey] = normalized;
      this.syncWorkflowCtxForColumn(columnKey, normalized);
      this.publishWidgetDataIfEnabled();
      return;
    }

    row[columnKey] = value ?? '';
    this.syncWorkflowCtxForColumn(columnKey, row[columnKey]);
    this.publishWidgetDataIfEnabled();
  }

  getInputDisplayValue(columnKey: string, value: any, row?: RowData): any {
    if (!this.isNumericColumn(columnKey, row)) {
      return value ?? '';
    }

    return this.formatNumericForDisplay(value);
  }

  getCellDisplayValue(columnKey: string, value: any, row?: RowData): any {
    if (!this.isNumericColumn(columnKey, row)) {
      return value ?? '';
    }

    return this.formatNumericForDisplay(value);
  }

  private initializeTable(): void {
    this.filterSubscription?.unsubscribe();
    this.widgetDataSubscription?.unsubscribe();
    this.resetStateSubscription?.unsubscribe();
    this.selectedRowsByValue.clear();

    const headerColumns: HeaderColumnConfig[] = this.data?.headerColumns || [];
    const dropdownColumns: HeaderColumnConfig[] = this.data?.dropdownColumns || [];

    this.displayedColumns = headerColumns.map((column) => column.key);
    this.columnLabels = Object.fromEntries(
      headerColumns.map((column) => [column.key, column.label || column.key])
    );
    this.columnConfigMap = Object.fromEntries(
      headerColumns
        .filter((column) => column && column.key)
        .map((column) => [column.key, { ...column }])
    );

    const dropdownMap: Record<string, HeaderColumnConfig> = {};

    headerColumns.forEach((column) => {
      if (column.type === 'dropdown' || column.isDropdown) {
        dropdownMap[column.key] = { ...column };
      }
    });

    dropdownColumns.forEach((column) => {
      dropdownMap[column.key] = {
        ...(dropdownMap[column.key] || {}),
        ...column,
        type: 'dropdown'
      };
    });

    const sourceRows: RowData[] = this.data?.tableData || this.data?.rows || [];
    this.baseDataSource = sourceRows.map((row) => ({ ...row }));
    this.dataSource = this.baseDataSource.map((row) => ({ ...row }));
    this.dropdownColumnConfig = dropdownMap;
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.prefillRowsFromWorkflowCtx(this.dataSource);

    const restoredFromExternalWidgetData = this.bindToExternalWidgetData();
    if (!restoredFromExternalWidgetData) {
      this.publishWidgetDataIfEnabled();
    }

    this.buildMappingRowsByValue();
    this.dynamicFilterKey = this.data?.filterKey || this.data?.discountTypeFilterKey || '';
    this.selectionMode = this.data?.selectionMode === 'append' ? 'append' : 'replace';

    this.resetStateSubscription = this.globalFilterService.resetState$.subscribe(() => {
      this.resetTableState();
    });

    if (this.dynamicFilterKey && Object.keys(this.mappingRowsByValue).length > 0) {
      this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
        const shouldHandleChange =
          !filters?.changedFilterKey || filters.changedFilterKey === this.dynamicFilterKey;

        if (!shouldHandleChange) {
          return;
        }

        const selectedValues = this.extractSelectedValues(filters, this.dynamicFilterKey);
        if (this.shouldRetainRowsOnEmptySelection(selectedValues)) {
          return;
        }
        this.applySelections(selectedValues);
      });

      const currentFilters = this.globalFilterService.getCurrentFilters();
      const currentSelections = this.extractSelectedValues(currentFilters, this.dynamicFilterKey);
      if (!restoredFromExternalWidgetData) {
        this.applySelections(currentSelections);
      }
    }
  }

  private normalizeValueForColumn(columnKey: string, value: any): any {
    const isMultiSelectColumn = this.isMultiSelect(columnKey);

    if (isMultiSelectColumn) {
      if (Array.isArray(value)) {
        return value
          .map((item) => String(item).trim())
          .filter((item) => item.length > 0);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0);
      }

      return [];
    }

    if (Array.isArray(value)) {
      return value[0] ?? '';
    }

    return value ?? '';
  }

  private isLikelyNumericColumn(columnKey: string): boolean {
    const normalizedKey = String(columnKey || '').trim().toLowerCase();
    const normalizedLabel = String(this.columnLabels?.[columnKey] || '')
      .trim()
      .toLowerCase();

    if (!normalizedKey && !normalizedLabel) {
      return false;
    }

    if (this.nonNumericColumnHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint))) {
      return false;
    }

    if (normalizedLabel.includes('%') || normalizedKey.includes('%')) {
      return true;
    }

    return this.numericColumnHints.some((hint) => normalizedKey.includes(hint) || normalizedLabel.includes(hint));
  }

  private isNumericValue(value: any): boolean {
    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }

    if (typeof value !== 'string') {
      return false;
    }

    const cleaned = value.replace(/[%,]/g, '').trim();
    if (!cleaned) {
      return false;
    }

    return !Number.isNaN(Number(cleaned));
  }

  private normalizeNumericForStorage(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    const raw = String(value).replace(/,/g, '').trim();
    if (!raw) {
      return '';
    }

    if (raw.endsWith('%')) {
      return raw;
    }

    const sign = raw.startsWith('-') ? '-' : '';
    const unsigned = sign ? raw.slice(1) : raw;
    const parts = unsigned.split('.');
    const integerPart = (parts[0] || '').replace(/\D/g, '');
    const fractionalPart = parts.length > 1 ? parts.slice(1).join('').replace(/\D/g, '') : '';

    if (!integerPart && !fractionalPart) {
      return '';
    }

    if (fractionalPart.length > 0) {
      return `${sign}${integerPart || '0'}.${fractionalPart}`;
    }

    return `${sign}${integerPart}`;
  }

  private formatNumericForDisplay(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    const raw = String(value).trim();
    if (!raw) {
      return '';
    }

    if (raw.endsWith('%')) {
      return raw;
    }

    const normalized = this.normalizeNumericForStorage(raw);
    if (!normalized) {
      return raw;
    }

    const sign = normalized.startsWith('-') ? '-' : '';
    const unsigned = sign ? normalized.slice(1) : normalized;
    const parts = unsigned.split('.');
    const integerPart = parts[0] || '0';
    const fractionalPart = parts.length > 1 ? parts[1] : '';
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (fractionalPart.length > 0) {
      return `${sign}${groupedInteger}.${fractionalPart}`;
    }

    return `${sign}${groupedInteger}`;
  }

  private normalizeDropdownValuesForRows(rows: RowData[]): void {
    rows.forEach((row) => {
      Object.keys(this.dropdownColumnConfig).forEach((columnKey) => {
        row[columnKey] = this.normalizeValueForColumn(columnKey, row[columnKey]);
      });
    });
  }

  private buildMappingRowsByValue(): void {
    this.mappingRowsByValue = {};

    const genericMappings = Array.isArray(this.data?.selectionMappings)
      ? (this.data.selectionMappings as SelectionMappingConfig[])
      : [];

    if (genericMappings.length > 0) {
      genericMappings.forEach((mapping) => {
        const parsed = this.parseSelectionMappingRows(mapping);
        if (!parsed) {
          return;
        }
        this.mappingRowsByValue[this.normalizeKey(parsed.triggerValue)] = parsed.rows;
      });
      return;
    }

    this.buildLegacyDiscountMappings();
  }

  private buildLegacyDiscountMappings(): void {
    const rawMappings = this.data?.discountTypeMappings;

    if (Array.isArray(rawMappings)) {
      rawMappings.forEach((mapping: any) => {
        const parsed = this.parseLegacyDiscountMapping(mapping);
        if (!parsed) {
          return;
        }
        const triggerKey = this.normalizeKey(parsed.discountType);
        this.mappingRowsByValue[triggerKey] = [this.createRowFromLegacyMapping(parsed)];
      });
      return;
    }

    if (rawMappings && typeof rawMappings === 'object') {
      Object.entries(rawMappings).forEach(([discountType, mapping]) => {
        const parsed = this.parseLegacyDiscountMapping({ ...(mapping as any), discountType });
        if (!parsed) {
          return;
        }
        const triggerKey = this.normalizeKey(parsed.discountType);
        this.mappingRowsByValue[triggerKey] = [this.createRowFromLegacyMapping(parsed)];
      });
    }
  }

  private parseSelectionMappingRows(
    mapping: SelectionMappingConfig
  ): { triggerValue: string; rows: RowData[] } | null {
    if (!mapping || typeof mapping !== 'object') {
      return null;
    }

    const triggerValue = String(
      mapping.value ?? mapping.triggerValue ?? mapping.selectionValue ?? ''
    ).trim();

    if (!triggerValue) {
      return null;
    }

    let rows: RowData[] = [];
    if (Array.isArray(mapping.rows)) {
      rows = mapping.rows
        .filter((row) => row && typeof row === 'object')
        .map((row) => ({ ...row }));
    } else if (mapping.row && typeof mapping.row === 'object') {
      rows = [{ ...mapping.row }];
    } else {
      const { value, triggerValue: _triggerValue, selectionValue, row, rows: _rows, ...rest } =
        mapping as any;
      if (Object.keys(rest).length > 0) {
        rows = [{ ...rest }];
      }
    }

    if (rows.length === 0) {
      return null;
    }

    return {
      triggerValue,
      rows: rows.map((row) => this.buildDisplayRow(row))
    };
  }

  private parseLegacyDiscountMapping(mapping: any): LegacyDiscountMappingConfig | null {
    if (!mapping) {
      return null;
    }

    const discountType = String(mapping.discountType || mapping.discount_type || '').trim();
    if (!discountType) {
      return null;
    }

    return {
      discountType,
      quoteLevelDiscount: String(
        mapping.quoteLevelDiscount ??
        mapping.quote_level_discount ??
        ''
      ).trim(),
      reasons: this.toStringArray(mapping.reasons || mapping.reasonOptions || mapping.options),
      defaultSelectedReason: mapping.defaultSelectedReason,
      defaultSelectedReasons: this.toStringArray(mapping.defaultSelectedReasons)
    };
  }

  private toStringArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }

  private extractSelectedValues(filters: any, filterKey: string): string[] {
    const rawValue = filters?.requestParams?.[filterKey] ?? filters?.queryParams?.[filterKey];

    if (Array.isArray(rawValue)) {
      const flattened = rawValue.flatMap((value) => (Array.isArray(value) ? value : [value]));
      return flattened
        .map((value) => (value === undefined || value === null ? '' : String(value).trim()))
        .filter((value) => value.length > 0);
    }

    const singleValue = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();
    return singleValue ? [singleValue] : [];
  }

  private applySelections(selectedValues: string[]): void {
    const normalizedSelectedKeys = Array.from(
      new Set(
        selectedValues
          .map((value) => this.normalizeKey(value))
          .filter((value) => value.length > 0)
      )
    );

    if (this.selectionMode === 'replace') {
      this.selectedRowsByValue.clear();
      normalizedSelectedKeys.forEach((normalizedKey) => {
        const rows = this.mappingRowsByValue[normalizedKey];
        if (rows?.length) {
          this.selectedRowsByValue.set(normalizedKey, this.cloneRows(rows));
        }
      });
      this.rebuildTableData();
      return;
    }

    const selectedSet = new Set(normalizedSelectedKeys);
    Array.from(this.selectedRowsByValue.keys()).forEach((key) => {
      if (!selectedSet.has(key)) {
        this.selectedRowsByValue.delete(key);
      }
    });

    normalizedSelectedKeys.forEach((normalizedKey) => {
      const rows = this.mappingRowsByValue[normalizedKey];
      if (rows?.length) {
        this.selectedRowsByValue.set(normalizedKey, this.cloneRows(rows));
      }
    });

    this.rebuildTableData();
  }

  private rebuildTableData(): void {
    const baseRows = this.baseDataSource.map((row) => ({ ...row }));
    const mappedRows = Array.from(this.selectedRowsByValue.values())
      .flat()
      .map((row) => ({ ...row }));

    this.dataSource = this.dedupeRows([...baseRows, ...mappedRows]);
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.publishWidgetDataIfEnabled();
  }

  private createRowFromLegacyMapping(mapping: LegacyDiscountMappingConfig): RowData {
    const discountTypeColumnKey = this.data?.discountTypeColumnKey || 'discount_type';
    const reasonColumnKey = this.data?.reasonColumnKey || 'reason';
    const quoteLevelDiscountColumnKey =
      this.data?.quoteLevelDiscountColumnKey || 'quote_level_discount';
    const reasonOptionsField =
      this.dropdownColumnConfig?.[reasonColumnKey]?.optionsField || `${reasonColumnKey}Options`;

    const reasonValue = this.isMultiSelect(reasonColumnKey)
      ? (mapping.defaultSelectedReasons?.length ? mapping.defaultSelectedReasons : mapping.reasons)
      : (mapping.defaultSelectedReason || mapping.reasons[0] || '');

    const row: RowData = {
      [discountTypeColumnKey]: mapping.discountType,
      [reasonColumnKey]: reasonValue,
      [quoteLevelDiscountColumnKey]: mapping.quoteLevelDiscount,
      [reasonOptionsField]: mapping.reasons
    };

    return this.buildDisplayRow(row);
  }

  private buildDisplayRow(row: RowData): RowData {
    const displayRow: RowData = { ...row };
    this.displayedColumns.forEach((columnKey) => {
      if (displayRow[columnKey] === undefined || displayRow[columnKey] === null) {
        displayRow[columnKey] = '';
      }
    });
    return displayRow;
  }

  private cloneRows(rows: RowData[]): RowData[] {
    return rows.map((row) => ({ ...row }));
  }

  private normalizeKey(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private bindToExternalWidgetData(): boolean {
    if (!this.data?.useWidgetData || !this.data?.widgetId) {
      return false;
    }

    let restoredFromWidgetData = false;
    const initialRows = this.globalFilterService.getWidgetData(this.data.widgetId);
    if (initialRows.length > 0) {
      this.updateRowsFromExternalWidgetData(initialRows);
      restoredFromWidgetData = true;
    }

    this.widgetDataSubscription = this.globalFilterService.widgetData$.subscribe((widgetDataMap) => {
      const rows = widgetDataMap?.[this.data.widgetId];
      if (!Array.isArray(rows)) {
        return;
      }

      this.updateRowsFromExternalWidgetData(rows);
    });

    return restoredFromWidgetData;
  }

  private updateRowsFromExternalWidgetData(rows: RowData[]): void {
    // Keep configured base rows intact; external rows are the current table state.
    // If we overwrite base rows here, select-all/deselect cycles can duplicate mapped rows.
    this.dataSource = this.dedupeRows(rows.map((row) => ({ ...row })));
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.prefillRowsFromWorkflowCtx(this.dataSource);
  }

  private prefillRowsFromWorkflowCtx(rows: RowData[]): void {
    const workflowCtxMap = this.data?.workflowCtxMap;
    if (!workflowCtxMap || typeof workflowCtxMap !== 'object') {
      return;
    }

    const workflowCtx = this.globalFilterService.getWorkflowCtx<Record<string, any>>() || {};
    const entries = Object.entries(workflowCtxMap as Record<string, any>)
      .map(([columnKey, ctxKey]) => [String(columnKey).trim(), String(ctxKey).trim()] as const)
      .filter(([columnKey, ctxKey]) => columnKey.length > 0 && ctxKey.length > 0);

    if (!entries.length) {
      return;
    }

    rows.forEach((row) => {
      if (!row || typeof row !== 'object') {
        return;
      }

      entries.forEach(([columnKey, ctxKey]) => {
        const currentValue = row[columnKey];
        const ctxValue = workflowCtx[ctxKey];

        const isEmpty =
          currentValue === undefined ||
          currentValue === null ||
          (typeof currentValue === 'string' && currentValue.trim() === '');

        if (!isEmpty) {
          return;
        }

        if (ctxValue === undefined || ctxValue === null) {
          return;
        }

        const stringified = typeof ctxValue === 'string' ? ctxValue : String(ctxValue);
        if (stringified.trim() === '') {
          return;
        }

        row[columnKey] = ctxValue;
      });
    });
  }

  private syncWorkflowCtxForColumn(columnKey: string, value: any): void {
    const workflowCtxMap = this.data?.workflowCtxMap;
    if (!workflowCtxMap || typeof workflowCtxMap !== 'object') {
      return;
    }

    const ctxKeyRaw = (workflowCtxMap as Record<string, any>)[columnKey];
    const ctxKey = typeof ctxKeyRaw === 'string' ? ctxKeyRaw.trim() : '';
    if (!ctxKey) {
      return;
    }

    this.globalFilterService.setWorkflowCtx({ [ctxKey]: value });
  }

  private shouldRetainRowsOnEmptySelection(selectedValues: string[]): boolean {
    const retainOnEmptySelection = this.data?.retainOnEmptySelection === true;
    const hasNoSelection = !Array.isArray(selectedValues) || selectedValues.length === 0;

    return retainOnEmptySelection && hasNoSelection;
  }

  private dedupeRows(rows: RowData[]): RowData[] {
    if (!Array.isArray(rows) || rows.length <= 1) {
      return Array.isArray(rows) ? rows : [];
    }

    const seen = new Set<string>();
    const deduped: RowData[] = [];

    rows.forEach((row) => {
      const signature = this.buildRowSignature(row);
      if (seen.has(signature)) {
        return;
      }
      seen.add(signature);
      deduped.push(row);
    });

    return deduped;
  }

  private buildRowSignature(row: RowData): string {
    const source = row && typeof row === 'object' ? row : {};
    const signatureColumns = this.displayedColumns.length > 0
      ? this.displayedColumns
      : Object.keys(source);

    const signatureObject = signatureColumns.reduce((acc: Record<string, any>, columnKey: string) => {
      acc[columnKey] = source[columnKey] ?? '';
      return acc;
    }, {});

    return JSON.stringify(signatureObject);
  }

  private resetTableState(): void {
    this.selectedRowsByValue.clear();

    const sourceRows: RowData[] = this.data?.tableData || this.data?.rows || [];
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) {
      this.baseDataSource = [];
      this.dataSource = [];
      this.publishWidgetDataIfEnabled();
      return;
    }

    const emptyRows = sourceRows.map(() => {
      const row: RowData = {};
      this.displayedColumns.forEach((columnKey) => {
        row[columnKey] = this.isMultiSelect(columnKey) ? [] : '';
      });
      return row;
    });

    this.baseDataSource = emptyRows.map((row) => ({ ...row }));
    this.dataSource = this.baseDataSource.map((row) => ({ ...row }));
    this.normalizeDropdownValuesForRows(this.dataSource);
    this.publishWidgetDataIfEnabled();
  }

  private publishWidgetDataIfEnabled(): void {
    if (!this.data?.publishWidgetData || !this.data?.widgetId) {
      return;
    }

    const rows = this.dataSource.map((row) => ({ ...row }));
    this.globalFilterService.setWidgetData(this.data.widgetId, rows);
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.widgetDataSubscription?.unsubscribe();
    this.resetStateSubscription?.unsubscribe();
  }
}
